import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { getTeamLabel } from '../utils/teamLabels'
import { DEFAULT_LOGO, NAOMH_MEARNOG_LOGO, getClubLogo } from '../clubLogos'

function formatDate(dateStr) {
  if (!dateStr) return { month: '—', day: '—', time: '', full: '' }
  const d = new Date(dateStr)
  return {
    month: d.toLocaleString('en-IE', { month: 'short', timeZone: 'UTC' }),
    day: d.getUTCDate(),
    time: d.toLocaleString('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }),
    full: d.toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
}

function totalPts(goals, points) { return goals * 3 + points }

function getResult(f) {
  const home = totalPts(f.home_goals, f.home_points)
  const away = totalPts(f.away_goals, f.away_points)
  const diff = Math.abs(home - away)
  if (home > away) return { label: 'W', color: '#3B6D11', bg: '#EAF3DE', text: `Won by ${diff} pt${diff !== 1 ? 's' : ''}` }
  if (home < away) return { label: 'L', color: '#A32D2D', bg: '#FCEBEB', text: `Lost by ${diff} pt${diff !== 1 ? 's' : ''}` }
  return { label: 'D', color: '#854F0B', bg: '#FAEEDA', text: 'Draw' }
}

function gameTypeBadge(type) {
  const map = {
    league: { bg: '#E6F1FB', color: '#185FA5' },
    championship: { bg: '#FAEEDA', color: '#854F0B' },
    cup: { bg: '#EAF3DE', color: '#3B6D11' },
    friendly: { bg: '#F1EFE8', color: '#5F5E5A' }
  }
  return map[type] || map.league
}

function gameTypeLabel(type) {
  return { league: 'League', championship: 'Championship', cup: 'Cup', friendly: 'Friendly' }[type] || type
}

export default function Dashboard({ session, profile, onOpenMatch }) {
  const [squads, setSquads] = useState([])
  const [selectedSquadId, setSelectedSquadId] = useState(null)
  const [fixtures, setFixtures] = useState([])
  const [selectedSeason, setSelectedSeason] = useState(new Date().getFullYear())
  const [events, setEvents] = useState([])
  const [lineups, setLineups] = useState([])
  const [halfLengthMins, setHalfLengthMins] = useState(30)
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(false)
  const [filterTeam, setFilterTeam] = useState('all')
  const [filterSport, setFilterSport] = useState('all')
  const [sortCol, setSortCol] = useState('total')
  const [sortDir, setSortDir] = useState('desc')

  // Load squads first
  useEffect(() => {
    if (!profile) return
    async function loadSquads() {
      const isAdmin = profile?.role === 'club_admin'
      let query = supabase.from('squads').select('*, squad_teams(team_number, label)').eq('club_id', profile.club_id).order('name')
      if (!isAdmin && profile?.squadIds?.length > 0) {
        query = query.in('id', profile.squadIds)
      } else if (!isAdmin) {
        setLoading(false); return
      }
      const { data } = await query
      const squadList = data || []
      setSquads(squadList)
      // Default to first squad the user is a member of, or first squad overall
      const mySquad = squadList.find(s => profile?.squadIds?.includes(s.id)) || squadList[0]
      if (mySquad) setSelectedSquadId(mySquad.id)
      else setLoading(false)
    }
    loadSquads()
  }, [profile])

  // Load fixtures + events whenever selected squad changes
  const loadData = useCallback(async () => {
    if (!selectedSquadId) return
    setLoading(true)
    const { data: fixtureData } = await supabase
      .from('fixtures')
      .select('*, squads(name, age_group, slug, team1_label, team2_label, current_grade)')
      .eq('squad_id', selectedSquadId)
      .order('fixture_date', { ascending: true })
    const allFixtures = fixtureData || []
    setFixtures(allFixtures)
    const grade = allFixtures[0]?.squads?.current_grade || allFixtures[0]?.squads?.age_group
    if (grade) {
      const { data: rulesData } = await supabase
        .from('age_grade_rules').select('half_length_mins').eq('grade', grade).maybeSingle()
      setHalfLengthMins(rulesData?.half_length_mins || 30)
    } else {
      setHalfLengthMins(30)
    }
    const completedIds = allFixtures.filter(f => f.status === 'completed').map(f => f.id)
    if (completedIds.length > 0) {
      setStatsLoading(true)
      const [{ data: eventData }, { data: lineupData }] = await Promise.all([
        supabase.from('match_events').select('*, players(id, full_name, jersey_number)').in('fixture_id', completedIds),
        supabase.from('lineups').select('*, players(id, full_name, jersey_number)').in('fixture_id', completedIds)
      ])
      setEvents(eventData || [])
      setLineups(lineupData || [])
      setStatsLoading(false)
    } else {
      setEvents([])
      setLineups([])
    }
    setLoading(false)
  }, [selectedSquadId])

  useEffect(() => { loadData() }, [loadData])

  // ── Derived data ──────────────────────────────────────────────────────────

  const activeSquad = squads.find(s => s.id === selectedSquadId)
  const availableSeasons = [...new Set(fixtures.map(f => f.season).filter(Boolean))].sort((a, b) => b - a)
  const seasonFixtures = selectedSeason ? fixtures.filter(f => f.season === selectedSeason) : fixtures
  const hasTeam2 = seasonFixtures.some(f => String(f.team_number) === '2')
  const hasHurling = seasonFixtures.some(f => f.sport === 'hurling')
  const hasFootball = seasonFixtures.some(f => f.sport === 'football')
  const showTeamFilter = hasTeam2
  const showSportFilter = hasHurling && hasFootball

  const filteredFixtures = seasonFixtures.filter(f => {
    if (filterTeam !== 'all' && String(f.team_number || 1) !== filterTeam) return false
    if (filterSport !== 'all' && f.sport !== filterSport) return false
    return true
  })

  const completed = filteredFixtures.filter(f => f.status === 'completed').sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))
  const upcoming = filteredFixtures.filter(f => f.status !== 'completed').sort((a, b) => {
    if (a.status === 'live' && b.status !== 'live') return -1
    if (b.status === 'live' && a.status !== 'live') return 1
    return new Date(a.fixture_date) - new Date(b.fixture_date)
  })

  // Season record
  const record = completed.reduce((acc, f) => {
    const r = getResult(f)
    if (r.label === 'W') acc.w++
    else if (r.label === 'L') acc.l++
    else acc.d++
    acc.gf += totalPts(f.home_goals, f.home_points)
    acc.ga += totalPts(f.away_goals, f.away_points)
    return acc
  }, { w: 0, d: 0, l: 0, gf: 0, ga: 0 })

  // Form guide — last 5 results
  const form = completed.slice(0, 5).reverse()

  // ── Player stats ──────────────────────────────────────────────────────────

  const completedFilteredIds = new Set(completed.map(f => f.id))

  const playerStats = {}

  function initPlayer(id, name, jersey) {
    if (!playerStats[id]) {
      playerStats[id] = {
        id, name, jersey,
        gamesStarted: 0, gamesAsSub: 0,
        goals: 0, points: 0, twoPointers: 0,
        goalsFromPlay: 0, pointsFromPlay: 0,
        freesWon: 0, freesConceded: 0,
        yellowCards: 0, blackCards: 0, redCards: 0,
        kickoutsWon: 0,
      }
    }
  }

  // Track distinct fixtures each player appeared in, to avoid double-counting
  // players subbed on more than once in the same match (e.g. black-card sin-bin returns)
  const startedFixtures = {}
  const subFixtures = {}

  // Process lineups for games played
  lineups.filter(l => completedFilteredIds.has(l.fixture_id)).forEach(l => {
    if (!l.players) return
    const pid = l.players.id
    initPlayer(pid, l.players.full_name, l.players.jersey_number)
    if (l.starting) {
      if (!startedFixtures[pid]) startedFixtures[pid] = new Set()
      startedFixtures[pid].add(l.fixture_id)
    }
  })

  // Process events
  events.filter(e => completedFilteredIds.has(e.fixture_id)).forEach(e => {
    if (!e.player_id || !e.players) return
    const pid = e.player_id
    if (!playerStats[pid]) initPlayer(pid, e.players.full_name, e.players.jersey_number)
    const p = playerStats[pid]
    if (e.event_type === 'sub_on') {
      if (!subFixtures[pid]) subFixtures[pid] = new Set()
      subFixtures[pid].add(e.fixture_id)
    }
    if (e.event_type === 'goal') { p.goals++; if (e.notes === 'from_play') p.goalsFromPlay++ }
    if (e.event_type === 'point') { p.points++; if (e.notes === 'from_play') p.pointsFromPlay++ }
    if (e.event_type === 'twopoint') { p.twoPointers++; if (e.notes === 'from_play') p.pointsFromPlay += 2 }
    if (e.event_type === 'free_won') p.freesWon++
    if (e.event_type === 'free_conceded') p.freesConceded++
    if (e.event_type === 'yellow_card') p.yellowCards++
    if (e.event_type === 'black_card') p.blackCards++
    if (e.event_type === 'red_card') p.redCards++
    if (e.event_type === 'kickout_won') p.kickoutsWon++
  })

  // Minutes on pitch: build each player's on/off timeline per fixture from
  // sub_on/sub_off events (handling multiple on/off cycles in one match,
  // e.g. black-card sin-bin returns), then sum durations across all fixtures.
  const matchLengthMins = halfLengthMins * 2
  const minutesByPlayer = {}
  const allRelevantPids = new Set([...Object.keys(startedFixtures), ...Object.keys(subFixtures)])
  allRelevantPids.forEach(pid => {
    let total = 0
    const fixtureIdsForPlayer = new Set([
      ...(startedFixtures[pid] || []),
      ...(subFixtures[pid] || []),
    ])
    fixtureIdsForPlayer.forEach(fid => {
      const started = startedFixtures[pid]?.has(fid)
      const playerEvents = events
        .filter(e => e.fixture_id === fid && e.player_id === pid && ['sub_on', 'sub_off'].includes(e.event_type))
        .sort((a, b) => (a.minute || 0) - (b.minute || 0))
      let onSince = started ? 0 : null
      playerEvents.forEach(e => {
        const minute = e.minute || 0
        if (e.event_type === 'sub_on') {
          onSince = minute
        } else if (e.event_type === 'sub_off') {
          if (onSince !== null) {
            total += Math.max(0, minute - onSince)
            onSince = null
          }
        }
      })
      if (onSince !== null) {
        total += Math.max(0, matchLengthMins - onSince)
      }
    })
    minutesByPlayer[pid] = total
  })

  const statsList = Object.values(playerStats).map(p => ({
    ...p,
    gamesStarted: startedFixtures[p.id]?.size || 0,
    gamesAsSub: subFixtures[p.id]?.size || 0,
    gamesPlayed: new Set([...(startedFixtures[p.id] || []), ...(subFixtures[p.id] || [])]).size,
    minutesPlayed: minutesByPlayer[p.id] || 0,
    total: p.goals * 3 + p.points + p.twoPointers * 2,
    totalFromPlay: p.goalsFromPlay * 3 + p.pointsFromPlay,
  })).filter(p => p.gamesPlayed > 0)

  function sortStats(list) {
    return [...list].sort((a, b) => {
      const av = a[sortCol] ?? 0
      const bv = b[sortCol] ?? 0
      return sortDir === 'desc' ? bv - av : av - bv
    })
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortCol(col); setSortDir('desc') }
  }

  function SortHeader({ col, label, style }) {
    const active = sortCol === col
    return (
      <th onClick={() => handleSort(col)}
        style={{ ...thStyle, cursor: 'pointer', color: active ? '#185FA5' : '#888', userSelect: 'none', ...style }}>
        {label}{active ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
      </th>
    )
  }

  const sortedStats = sortStats(statsList)

  if (loading && squads.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
        <p>Loading...</p>
      </div>
    )
  }

  if (squads.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
        <p style={{ fontSize: '32px', marginBottom: '12px' }}>📊</p>
        <p>No squads assigned yet.</p>
      </div>
    )
  }

  const activePhases = ['playing', 'ht', 'et1', 'et_ht', 'et2']

  return (
    <div>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <h2 style={{ fontSize: '17px', fontWeight: '500', flex: 1, margin: 0 }}>Dashboard</h2>
        {squads.length > 1 && (
          <select
            value={selectedSquadId || ''}
            onChange={e => setSelectedSquadId(e.target.value)}
            style={{ padding: '6px 10px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '13px', background: '#f8f8f8', color: '#333' }}>
            {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {activeSquad?.slug && (
          <span
            onClick={() => window.open('https://gaamatchtracker.ie/public/' + activeSquad.slug, '_blank')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#E6F1FB', color: '#185FA5', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            🔗 Share Live Score Page
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}><p>Loading...</p></div>
      ) : (
        <>
          {/* Filters */}
          {(showTeamFilter || showSportFilter || availableSeasons.length > 1) && (
            <div style={{ padding: '8px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {availableSeasons.length > 1 && (
                <select value={selectedSeason} onChange={e => setSelectedSeason(Number(e.target.value))}
                  style={{ padding: '6px 8px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '12px', background: '#f8f8f8' }}>
                  {availableSeasons.map(s => <option key={s} value={s}>{s} Season</option>)}
                </select>
              )}
              {showSportFilter && (
                <>
                  <button onClick={() => setFilterSport('all')} style={filterBtn(filterSport === 'all')}>All Sports</button>
                  <button onClick={() => setFilterSport('football')} style={filterBtn(filterSport === 'football')}>Football</button>
                  <button onClick={() => setFilterSport('hurling')} style={filterBtn(filterSport === 'hurling')}>Hurling</button>
                </>
              )}
              {showTeamFilter && (
                <>
                  <button onClick={() => setFilterTeam('all')} style={filterBtn(filterTeam === 'all')}>All Teams</button>
                  <button onClick={() => setFilterTeam('1')} style={filterBtn(filterTeam === '1')}>{getTeamLabel(1, activeSquad?.squad_teams)}</button>
                  <button onClick={() => setFilterTeam('2')} style={filterBtn(filterTeam === '2')}>{getTeamLabel(2, activeSquad?.squad_teams)}</button>
                </>
              )}
            </div>
          )}

        {seasonFixtures.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              <p style={{ fontSize: '32px', marginBottom: '12px' }}>📊</p>
              <p>No fixtures yet for {activeSquad?.name}.</p>
              <p style={{ fontSize: '12px', marginTop: '6px', color: '#aaa' }}>Add fixtures in the Fixtures tab to see stats here.</p>
            </div>
          ) : (
            <>
              {/* Season record */}
              {completed.length > 0 && (
                <div style={{ margin: '12px 16px', background: '#185FA5', borderRadius: '12px', padding: '14px 16px', color: 'white' }}>
                  <p style={{ fontSize: '10px', opacity: 0.7, letterSpacing: '0.4px', margin: '0 0 10px', fontWeight: '500' }}>
                    {activeSquad?.name?.toUpperCase()} · SEASON RECORD
                  </p>
                  <div style={{ display: 'flex', gap: '0', justifyContent: 'space-between' }}>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#EAF3DE' }}>{record.w}</p>
                      <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>Won</p>
                    </div>
                    <div style={{ width: '0.5px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }} />
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#FAEEDA' }}>{record.d}</p>
                      <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>Drawn</p>
                    </div>
                    <div style={{ width: '0.5px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }} />
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#FCEBEB' }}>{record.l}</p>
                      <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>Lost</p>
                    </div>
                    <div style={{ width: '0.5px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }} />
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', margin: '7px 0 0' }}>{record.gf}</p>
                      <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>Pts For</p>
                    </div>
                    <div style={{ width: '0.5px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }} />
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: '600', margin: '7px 0 0' }}>{record.ga}</p>
                      <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>Pts Against</p>
                    </div>
                  </div>

                  {/* Form guide */}
                  {form.length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '0.5px solid rgba(255,255,255,0.2)' }}>
                      <p style={{ fontSize: '9px', opacity: 0.6, letterSpacing: '0.3px', margin: '0 0 6px' }}>RECENT FORM</p>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {form.map((f, i) => {
                          const r = getResult(f)
                          return (
                            <div key={i} title={`vs ${f.opponent} — ${r.text}`}
                              style={{ width: '26px', height: '26px', borderRadius: '50%', background: r.label === 'W' ? '#3B6D11' : r.label === 'L' ? '#A32D2D' : '#854F0B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white' }}>
                              {r.label}
                            </div>
                          )
                        })}
                        {Array.from({ length: Math.max(0, 5 - form.length) }).map((_, i) => (
                          <div key={`empty-${i}`} style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.2)' }} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Upcoming fixtures */}
              {upcoming.length > 0 && (
                <div style={{ padding: '12px 16px 0' }}>
                  <p style={sectionLabel}>UPCOMING FIXTURES</p>
                  {upcoming.slice(0, 3).map(f => {
                    const date = formatDate(f.fixture_date)
                    const badge = gameTypeBadge(f.game_type)
                    const oppLogo = getClubLogo(f.opponent)
                    const isLive = f.status === 'live' && (f.clock_running || activePhases.includes(f.match_phase))
                    const isLineupConfirmed = f.status === 'live' && !isLive
                    const phaseLabel = f.match_phase === 'ht' ? '🔴 LIVE · HT' : f.match_phase === 'et_ht' ? '🔴 LIVE · ET HT' : '🔴 LIVE'
                    return (
                      <div key={f.id} onClick={() => onOpenMatch(f)}
                        style={{ background: 'white', borderRadius: '12px', padding: '12px', marginBottom: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ textAlign: 'center', minWidth: '32px' }}>
                          <p style={{ fontSize: '10px', color: '#888', margin: 0 }}>{date.month}</p>
                          <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1, margin: 0 }}>{date.day}</p>
                        </div>
                        <img src={oppLogo} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'contain', flexShrink: 0 }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>vs {f.opponent}</p>
                          <p style={{ fontSize: '11px', color: '#888', margin: '1px 0' }}>{date.time}{f.venue ? ` · ${f.venue}` : ''}</p>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: isLive ? '#FF3B30' : isLineupConfirmed ? '#3B6D11' : '#FAEEDA', color: isLive ? 'white' : isLineupConfirmed ? 'white' : '#854F0B', fontWeight: '500' }}>
                              {isLive ? phaseLabel : isLineupConfirmed ? '✅ Lineup' : 'Upcoming'}
                            </span>
                            <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: badge.bg, color: badge.color, fontWeight: '500' }}>{gameTypeLabel(f.game_type)}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Recent results */}
              {completed.length > 0 && (
                <div style={{ padding: '12px 16px 0' }}>
                  <p style={sectionLabel}>RECENT RESULTS</p>
                  {completed.slice(0, 3).map(f => {
                    const date = formatDate(f.fixture_date)
                    const result = getResult(f)
                    const badge = gameTypeBadge(f.game_type)
                    const oppLogo = getClubLogo(f.opponent)
                    const fEvents = events.filter(e => e.fixture_id === f.id)
                    const scorerMap = {}
                    fEvents.filter(e => ['goal','point','twopoint'].includes(e.event_type)).forEach(e => {
                      const name = e.players?.full_name || 'Unknown'
                      if (!scorerMap[name]) scorerMap[name] = { goals: 0, points: 0 }
                      if (e.event_type === 'goal') scorerMap[name].goals++
                      if (e.event_type === 'point') scorerMap[name].points++
                      if (e.event_type === 'twopoint') scorerMap[name].points += 2
                    })
                    const topScorers = Object.entries(scorerMap)
                      .sort((a, b) => (b[1].goals * 3 + b[1].points) - (a[1].goals * 3 + a[1].points))
                      .slice(0, 2)
                    const hTotal = totalPts(f.home_goals, f.home_points)
                    const aTotal = totalPts(f.away_goals, f.away_points)
                    return (
                      <div key={f.id} onClick={() => onOpenMatch(f)}
                        style={{ background: 'white', borderRadius: '12px', padding: '12px', marginBottom: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <div style={{ textAlign: 'center', minWidth: '32px' }}>
                          <p style={{ fontSize: '10px', color: '#888', margin: 0 }}>{date.month}</p>
                          <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1, margin: 0 }}>{date.day}</p>
                        </div>
                        <img src={oppLogo} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'contain', flexShrink: 0, marginTop: '2px' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>vs {f.opponent}</p>
                          <p style={{ fontSize: '13px', fontWeight: '600', color: '#333', margin: '2px 0' }}>
                            {f.home_goals}-{String(f.home_points).padStart(2,'0')} ({hTotal}) v {f.away_goals}-{String(f.away_points).padStart(2,'0')} ({aTotal})
                          </p>
                          <p style={{ fontSize: '11px', color: result.color, margin: '1px 0', fontWeight: '500' }}>{result.text}</p>
                          {topScorers.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                              {topScorers.map(([name, s]) => (
                                <span key={name} style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
                                  ⚽ {name.split(' ').slice(-1)[0]} {s.goals > 0 ? `${s.goals}-${String(s.points).padStart(2,'0')}` : `0-${String(s.points).padStart(2,'0')}`}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px', background: result.bg, color: result.color }}>{result.label}</span>
                          <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: badge.bg, color: badge.color, fontWeight: '500' }}>{gameTypeLabel(f.game_type)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Player stats table */}
              {statsLoading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontSize: '13px' }}>Loading stats...</div>
              ) : sortedStats.length > 0 ? (
                <div style={{ padding: '12px 16px 24px' }}>
                  <p style={sectionLabel}>PLAYER STATS</p>
                  <div style={{ overflowX: 'auto', borderRadius: '12px', border: '0.5px solid #e0e0e0', background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '640px' }}>
                      <thead>
                        <tr style={{ background: '#f8f8f8', borderBottom: '0.5px solid #e0e0e0' }}>
                          <th style={{ ...thStyle, textAlign: 'left', paddingLeft: '12px', minWidth: '120px' }}>Player</th>
                          <SortHeader col="gamesPlayed" label="GP" />
                          <SortHeader col="minutesPlayed" label="MIN" />
                          <SortHeader col="gamesStarted" label="GS" />
                          <SortHeader col="gamesAsSub" label="Sub" />
                          <SortHeader col="goals" label="G" />
                          <SortHeader col="points" label="P" />
                          <SortHeader col="total" label="Total" />
                          <SortHeader col="totalFromPlay" label="Play" />
                          <SortHeader col="freesWon" label="FW" />
                          <SortHeader col="freesConceded" label="FC" />
                          <SortHeader col="yellowCards" label="🟨" />
                          <SortHeader col="blackCards" label="⬛" />
                          <SortHeader col="redCards" label="🟥" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedStats.map((p, i) => (
                          <tr key={p.id} style={{ borderBottom: '0.5px solid #f5f5f5', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ ...tdStyle, textAlign: 'left', paddingLeft: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '600', color: '#185FA5', flexShrink: 0 }}>
                                  {p.jersey || '—'}
                                </div>
                                <span style={{ fontSize: '12px', color: '#333' }}>{p.name}</span>
                              </div>
                            </td>
                            <td style={tdStyle}>{p.gamesPlayed || '—'}</td>
                            <td style={tdStyle}>{p.minutesPlayed || '—'}</td>
                            <td style={tdStyle}>{p.gamesStarted || '—'}</td>
                            <td style={tdStyle}>{p.gamesAsSub || '—'}</td>
                            <td style={{ ...tdStyle, color: '#185FA5', fontWeight: '500' }}>{p.goals || '—'}</td>
                            <td style={{ ...tdStyle, color: '#3B6D11', fontWeight: '500' }}>{p.points || '—'}</td>
                            <td style={{ ...tdStyle, fontWeight: '600', color: '#333' }}>
                              {p.goals > 0 || p.points > 0 ? `${p.goals}-${String(p.points + p.twoPointers * 2).padStart(2,'0')}` : '—'}
                            </td>
                            <td style={{ ...tdStyle, color: '#888' }}>
                              {p.totalFromPlay > 0 ? `${p.goalsFromPlay}-${String(p.pointsFromPlay).padStart(2,'0')}` : '—'}
                            </td>
                            <td style={{ ...tdStyle, color: '#0F6E56' }}>{p.freesWon || '—'}</td>
                            <td style={{ ...tdStyle, color: '#888' }}>{p.freesConceded || '—'}</td>
                            <td style={{ ...tdStyle, color: '#854F0B' }}>{p.yellowCards || '—'}</td>
                            <td style={{ ...tdStyle, color: '#333' }}>{p.blackCards || '—'}</td>
                            <td style={{ ...tdStyle, color: '#A32D2D' }}>{p.redCards || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize: '10px', color: '#aaa', marginTop: '6px', textAlign: 'right' }}>
                    GP = games played · GS = started · Sub = came on · FW = frees won · FC = frees conceded · Tap column header to sort
                  </p>
                </div>
              ) : completed.length > 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                  No player stats recorded yet — stats appear once match events are logged.
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  )
}

const sectionLabel = { fontSize: '10px', color: '#888', fontWeight: '600', letterSpacing: '0.4px', margin: '0 0 10px' }
const thStyle = { padding: '8px 6px', textAlign: 'center', fontSize: '11px', color: '#888', fontWeight: '500', whiteSpace: 'nowrap' }
const tdStyle = { padding: '8px 6px', textAlign: 'center', fontSize: '12px', color: '#555', whiteSpace: 'nowrap' }
function filterBtn(active) {
  return { padding: '4px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '500', background: active ? '#185FA5' : '#e8e8e8', color: active ? 'white' : '#555' }
}